import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiChannelsDeviceBehaviorPlugin } from '@shure/device-ui/channels/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import {
	DeviceMixerType,
	DeviceModel,
	LobeStyle,
	Placement
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { AutoPositionDeviceBehaviorPlugin } from '@shure/shared/angular/features/device-coverage/auto-position/data-access';
import {
	DevicePerspective,
	CoveragePerspectives,
	DeviceCoverageDeviceBehaviorPlugin,
	PERSPECTIVE_MAP
} from '@shure/shared/angular/features/device-coverage/data-access';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	LightsImage,
	LightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';
@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa720DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiChannelsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		CoverageImageProvider,
		LightsImageProvider,
		AutoPositionDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA7201FT, DeviceModel.MXA7202FT];

	constructor(protected readonly transloco: TranslocoService) {}

	public hasTalkerHeightInput(): boolean {
		return false;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.coverage'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.intellimix'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(([coverageLabel, channelsLabel, intellimixLabel, presetsLabel, settingsLabel]) => [
				{
					route: DeviceUiRoute.Coverage,
					label: coverageLabel
				},
				{
					route: DeviceUiRoute.Channels,
					label: channelsLabel
				},
				{
					route: DeviceUiRoute.Intellimix,
					label: intellimixLabel
				},
				{
					route: DeviceUiRoute.Presets,
					label: presetsLabel
				},
				{
					route: DeviceUiRoute.Settings,
					label: settingsLabel
				}
			])
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Coverage;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	// FIXME DJC need to pass placement map as an optional parameter into the device-image-device-behavior-service
	protected readonly DEVICE_PERSPECTIVE_IMAGE_MAP: Record<DevicePerspective, string> = {
		[DevicePerspective.Bottom]: 'bottom',
		[DevicePerspective.Top]: 'top',
		[DevicePerspective.Side]: 'side',
		[DevicePerspective.End]: 'front'
	};

	public getCoverageImage(
		model: DeviceModel,
		perspective: CoveragePerspectives,
		placement: Placement
	): CoverageImage {
		const devicePerspective = PERSPECTIVE_MAP[perspective][placement];
		const imageParam = this.DEVICE_PERSPECTIVE_IMAGE_MAP[devicePerspective];

		return {
			live: `device-${model.toLowerCase()}-${imageParam}.svg`,
			virtual: `device-${model.toLowerCase()}-${imageParam}-line.svg`,
			isImageRound: false
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.Supported;
	}

	public getLightsImage(): LightsImage {
		return {
			imageUrl: 'device-mxa7xx.svg'
		};
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return true;
	}

	public getMixerType(_model: DeviceModel): DeviceMixerType | undefined {
		return DeviceMixerType.Intellimix;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Aec, 1],
			[DspType.Nr, 2],
			[DspType.Deverb, 3],
			[DspType.Denoiser, 4],
			[DspType.Peq, 5],
			[DspType.Compressor, 6],
			[DspType.Delay, 7]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getChannelsChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2]
		]);
	}

	public usePostGateValues(): boolean {
		return true;
	}

	public getPlacementLobeStyle(perspective: CoveragePerspectives, placement: Placement): LobeStyle {
		if (perspective === CoveragePerspectives.TopDown && placement === Placement.WallVertical) {
			return LobeStyle.Stacked;
		}
		return LobeStyle.Ellipse;
	}

	public hasSideView(_placement: Placement): boolean {
		return true;
	}

	public supports3D(): boolean {
		return true;
	}

	public useAIAec(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDenoiser(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDeverb(_model: DeviceModel): boolean {
		return true;
	}

	public canResetChannels(): boolean {
		return true;
	}

	public supportsChannels(): boolean {
		return true;
	}
}
